<template>
  <div>
    <LazyLibBreadcrumb
      :items="breadCrumbItems"
      :show-transparent-background="false"
    />
    <ShopProductDetailHead
      v-if="
        $globalPageSettings?.shopConfig?.pdpConfiguration &&
        urlKey &&
        $shopStore &&
        locale
      "
      :key="`pdp-head-${urlKey}`"
      :generate-pdp-link="generatePdpLink"
      :company-name="$globalPageSettings?.companyName"
      :additional-tab-content="additionalTabContent"
      :product-enrichments="productEnrichments"
    >
      <template #contentSlotAboveTabs>
        <LibErrorBoundary>
          <LazyVisibleLibProductEnrichmentAboveTabSection
            :product-enrichments="productEnrichments"
            hydrate-on-visible
          />
        </LibErrorBoundary>
      </template>
      <template #contentSlotCenter>
        <LibErrorBoundary>
          <LazyVisibleLibProductEnrichmentCenterSection
            hydrate-on-visible
            :product-enrichments="productEnrichments"
          />
        </LibErrorBoundary>
      </template>
      <!-- Each tab renders only the enrichments of its own group (DBP-4500). -->
      <template #contentSlotTabElement="tabSlotProps">
        <LibErrorBoundary>
          <LazyVisibleLibProductEnrichmentTabElement
            hydrate-on-visible
            :product-enrichments="tabSlotProps.productEnrichments"
          />
        </LibErrorBoundary>
      </template>
    </ShopProductDetailHead>

    <LibErrorBoundary>
      <LazyVisibleLibProductEnrichmentFooterSection
        hydrate-on-visible
        :product-enrichments="productEnrichments"
      />
    </LibErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import type { NavigationItemInterface } from '@hypercodestudio/basler-components';

import { StatusCodes } from 'http-status-codes';
import type { NuxtError } from 'nuxt/app';
import { buildUrlString } from '~/utils/buildUrlString';
import { buildPdpUrl } from '~/utils/buildPdpUrl';
import { useSeoAlternateUrls } from '~/composables/useSeoAlternateUrls';
import { useProductEnrichments } from '~/composables/useProductEnrichments';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useHostname } from '~/composables/useHostname';
import { useProductAddonsStore } from '~/stores/productDetailPageStore/productAddonsStore';
import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';
import { getPdpAlternateLocales } from '~/utils/shop/getPdpAlternateLocales';
import { isShopPdpRoute } from '~/utils/shop/isShopPdpRoute';
import { usePdpConfiguration } from '~/composables/usePdpConfiguration';

const ProductDetailDistributors = defineAsyncComponent(
  () => import('~/components/shop/ProductDetailDistributors.vue')
);

const route = useRoute();
const urlKey = computed(() => String(route.params.urlKey));
const locale = useRouteParamLocale();
const logger = useLogger();
const {
  $globalPageSettings,
  $colorMode,
  $localeSlugMap,
  $availableContentfulLocaleCodes,
  $shopStore,
  $textDictionary,
  $secondaryNavigation
} = useNuxtApp();
const serverUrlOrigin = useHostname();

// The requested locale must resolve to a known Contentful code for a valid
// PDP page; otherwise return a 404. `$activeLocale.contentfulCode` always
// falls back to a default, so we check resolvability directly against the
// known Contentful codes (mirrors the previous `$resolvedLocale` undefined
// case, which existed exactly because the fallback is NOT included).
// @see https://gcp.baslerweb.com/jira/browse/WEB2-2478
const isLocaleResolvable = $availableContentfulLocaleCodes.value.some(
  (code) => code.toLowerCase() === locale.value.toLowerCase()
);
if (!isLocaleResolvable) {
  throw createError({
    statusCode: StatusCodes.NOT_FOUND,
    fatal: true
  });
}

useSeoAlternateUrls();

// Set $colorMode synchronously in setup so the layout's provide(INJECTED_COLOR_MODE,
// readonly($colorMode)) is already 'light' when HeroCanvas.client.vue and
// LazyLibBreadcrumb calls inject() during its own setup(). Without this, SPA navigation
// from a dark-themed page carries a stale 'dark' $colorMode: HeroCanvas then sets
// effect-opacity: 0, hiding the backdrop-filter that inverts the dark gradient to
// light-gray in the breadcrumb area.
$colorMode.value = 'light';

onMounted(() => {
  $secondaryNavigation.value = null;
});

const productStore = useProductDetailPageStore();
const vscPageLink = computed(() =>
  $globalPageSettings.value?.vscPage?.slug
    ? buildLinkInterface(
        buildUrlString(locale.value, $globalPageSettings.value.vscPage.slug),
        locale.value
      )
    : null
);

watchEffect(() =>
  productStore.$patch(() => {
    // TODO: remove and use {@link usePdpConfiguration}
    const pdpConfiguration = usePdpConfiguration();
    if (pdpConfiguration.value) {
      productStore.specificationConfiguration = pdpConfiguration.value;
    }
    productStore.urlKey = urlKey.value;
    productStore.vscPageLink = vscPageLink.value;
  })
);

/**
 * Maps a failed product init to the error the page should surface. Keeps the
 * upstream status (404 for unknown url keys, 503 for Magento outages) so the
 * error page and crawlers see the real cause.
 *
 * Must stay free of composables: it runs inside the `useAsyncData` handler
 * after an `await`, where no Nuxt instance is available.
 * @see https://baslerweb.atlassian.net/browse/DBP-4569
 */
function toPdpInitError(error: unknown): NuxtError {
  if (
    error &&
    typeof error === 'object' &&
    'statusCode' in error &&
    typeof error.statusCode === 'number'
  ) {
    return createError({
      statusCode: error.statusCode,
      cause: error,
      fatal: true
    });
  }

  return createError({
    statusCode: StatusCodes.INTERNAL_SERVER_ERROR,
    cause: error,
    fatal: true
  });
}

const productAddonStore = useProductAddonsStore();

function resetPdpStateForNavigation(targetUrlKey: string): void {
  const nuxtApp = useNuxtApp();
  const isInitialHydration =
    import.meta.client && nuxtApp.isHydrating && nuxtApp.payload.serverRendered;

  if (!isInitialHydration) {
    productStore.loading = true;
  }

  productAddonStore.resetForPdpNavigation();

  // Compare against the slug the product was requested for, not its Magento
  // `url_key`: for differently cased or renamed slugs Magento returns the
  // product with a 301 and `url_key` differs from the route by design.
  if (productStore.product && productStore.productUrlKey !== targetUrlKey) {
    productStore.product = null;
    productStore.productUrlKey = null;
    productStore.variant = null;
  }
}

// ALWAYS set loading at navigation start to prevent flash of stale content
resetPdpStateForNavigation(urlKey.value);

// Watch for URL changes and immediately reset stale PDP state
watch(urlKey, (targetUrlKey) => {
  resetPdpStateForNavigation(targetUrlKey);
});

onBeforeRouteLeave((to) => {
  // For PDP -> PDP navigation we must not invalidate the store generation here,
  // otherwise an in-flight init for the new PDP can become stale and leave the
  // head in a permanent loading/empty state.
  if (isShopPdpRoute(to)) {
    return;
  }

  productStore.cancelPendingLoads();
  productAddonStore.resetForPdpNavigation();
});

const pdpInit = useAsyncData(
  `pdp-init-${urlKey.value}`,
  async () => {
    const initUrlKey = urlKey.value;
    try {
      await productStore.init(route.query);
      return null;
    } catch (error) {
      // The user already left this PDP; the failure is not relevant anymore.
      if (!isShopPdpRoute(route, initUrlKey)) {
        return null;
      }

      throw toPdpInitError(error);
    }
  },
  {
    lazy: true,
    watch: [urlKey, () => route.query]
  }
);

// Start enrichments while the store exposes the running product init. Its SSR
// handler waits for that promise before reading product-dependent rules.
const productEnrichments = useProductEnrichments();
const { error: pdpInitError } = await pdpInit;

// `useAsyncData` swallows handler errors into `error`; without re-throwing, an
// unknown url key renders a PDP stuck in its loading state instead of the 404
// page. SSR and non-lazy loads surface here, client-side navigations via the
// watcher below.
if (pdpInitError.value && isShopPdpRoute(route, urlKey.value)) {
  throw pdpInitError.value;
}

watch(pdpInitError, (error) => {
  if (error && isShopPdpRoute(route, urlKey.value)) {
    showError(error);
  }
});

watch(
  () =>
    [
      productAddonStore.selectedRequiredItemUrlKey,
      productAddonStore.bundledProduct,
      productStore.loading,
      productStore.urlKey
    ] as const,
  async ([requiredItemUrlKey, bundledProduct, isLoading, storeUrlKey]) => {
    // XXX: this is a workaround for bundle products as the bundle
    // product does not provide suffucient information.
    // Therefore, we load the product from the first option
    // @see https://gcp.baslerweb.com/jira/browse/DBP-448
    if (
      !requiredItemUrlKey ||
      !bundledProduct ||
      storeUrlKey !== urlKey.value ||
      isLoading
    ) {
      return;
    }

    await productStore.loadProduct(requiredItemUrlKey);
    productAddonStore.ensureFirstRequiredItem();
  },
  { immediate: true }
);

watch(
  () =>
    [
      productStore.redirect?.urlKey,
      productStore.loading,
      productStore.urlKey
    ] as const,
  async ([redirectUrlKey, isLoading, storeUrlKey]) => {
    if (
      !redirectUrlKey ||
      redirectUrlKey === urlKey.value ||
      isLoading ||
      storeUrlKey !== urlKey.value ||
      !isShopPdpRoute(route, urlKey.value)
    ) {
      return;
    }

    const redirect = productStore.redirect;
    if (!redirect) {
      return;
    }

    try {
      await enhancedNavigateTo(
        {
          path: buildPdpUrl(locale.value, redirect.urlKey),
          query: route.query
        },
        {
          redirectCode: redirect.statusCode ?? StatusCodes.TEMPORARY_REDIRECT,
          external: false,
          replace: true
        }
      );
    } catch (e) {
      logger.error('failed to redirect on pdp with magento redirect', e);
    }
  },
  { immediate: true }
);

watch(
  () => productStore.isInvalidConfiguration,
  async (isInvalidConfiguration) => {
    if (
      !isInvalidConfiguration ||
      productStore.loading ||
      productStore.urlKey !== urlKey.value ||
      !isShopPdpRoute(route, urlKey.value)
    ) {
      return;
    }

    try {
      await enhancedNavigateTo(
        {
          path: buildPdpUrl(locale.value, urlKey.value),
          query: productStore.nextValidConfiguration
        },
        {
          redirectCode: StatusCodes.TEMPORARY_REDIRECT,
          external: false,
          replace: true
        }
      );
    } catch (e) {
      logger.error('failed to redirect on pdp with invalid configuration', e);
    }
  },
  { immediate: true }
);

const additionalTabContent = computed(() => [
  {
    id: 'tab-sources-of-supply',
    title:
      $textDictionary.value['pdp.sources-of-supply.tabPanel.title.label'] ??
      'Sources of supply',
    component: ProductDetailDistributors,
    visible: Boolean(
      productStore.decoratedProduct?.sku !== '' &&
      productStore.decoratedProduct?.distributorsData
    )
  }
]);

const breadCrumbItems = computed(() => {
  const options: NavigationItemInterface[] = [];

  options.push({
    text: 'Home',
    link: {
      uri: buildUrlString(locale.value, undefined, undefined, undefined, true)
    }
  });

  if (breadCrumbCategoryItems.value && breadCrumbCategoryItems?.value?.length) {
    breadCrumbCategoryItems.value.forEach((entry) => {
      options.push(entry);
    });
  } else {
    options.push({
      text:
        $textDictionary.value['globalPageSettings.productOverviewPage.label'] ??
        'Products',
      link: $globalPageSettings.value?.shopConfig?.productOverviewPage?.slug
        ? {
            uri: buildUrlString(
              locale.value,
              $globalPageSettings.value?.shopConfig.productOverviewPage.slug,
              undefined,
              undefined,
              true
            )
          }
        : {}
    });
  }

  options.push({
    text: productStore.decoratedProduct?.name || urlKey.value || '',
    link: { uri: buildPdpUrl(locale.value, urlKey.value, true) } // XXX: We have to set this field to undefined instead of removing it because without it the text is not shown
  });

  return options;
});

// Category breadcrumbs are resolved server-side inside `/api/magento/product`
// (the Contentful slug lookups run in-process and are cached; unmapped
// categories are skipped). They travel with the product, so the page never
// issues a `slugById` round-trip itself and no internal 404s leak into the
// SSR/hydration cycle.
const breadCrumbCategoryItems = computed<NavigationItemInterface[]>(
  () => productStore.breadcrumbs
);

watch(
  // do not use active product here, as a configured product might
  // have a different url key than the initial (unconfigured) product
  () => productStore.product?.url_key,
  () => {
    const urlKey = productStore.product?.url_key;
    if (!urlKey) {
      $localeSlugMap.value = {};

      return;
    }

    const pdpConfiguration = usePdpConfiguration();
    const availableLocales = getPdpAlternateLocales({
      locales: $availableContentfulLocaleCodes.value ?? [],
      currentLocale: locale.value,
      customAttributes:
        productStore.activeProduct?.custom_attributesV2?.items ?? [],
      localeStoreMap: pdpConfiguration.value?.localeStoreMap
    });

    $localeSlugMap.value = availableLocales.reduce(
      (collector, code) => {
        collector[code] = buildPdpUrl('', urlKey, false);

        return collector;
      },
      {} as Record<string, string>
    );
  },
  { immediate: true }
);

// Currently a separate function as this is used to create metadata
// which requires an absolute url.
function generatePdpLink(locale: string, p: DecoratedProduct) {
  return buildPdpUrl(locale, p.urlKey!, serverUrlOrigin);
}

const breadcrumbList = computed<WithContext<BreadcrumbList>>(() => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadCrumbItems.value.map<ListItem>((item, index) => ({
    '@type': 'ListItem',
    name: item.text,
    position: index + 1,
    item: typeof item.link?.uri === 'string' ? item.link?.uri : ''
  }))
}));

useHead({
  script: [
    {
      key: 'pdp-breadcrumb-structured-data',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbList.value),
      tagPriority: -1
    }
  ]
});
</script>
